import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { formatVoucherDuration } from './durationUtil';
import { buildHotspotLoginUrl } from '../../utils/voucher_qr';

/**
 * QRVoucherTemplate (2026-07-21)
 * Portrait card dengan QR code — 7 kolom × 5 baris = 35 voucher/halaman A4 landscape.
 *
 * Content per card:
 *   [Logo Kumara]
 *   KODE VOUCHER
 *   XXXXXX (kode besar)
 *   [QR code voucher]
 *   ── divider ──
 *   Generated on YYYY-MM-DD
 *   Distributed by COMPANY_NAME
 *   Rp. XX.XXX / N Hari (banner)
 */
const QRVoucherTemplate = ({ vouchers = [], companyLogo = '', companyName = '', hotspotGatewayIp, batchHeader, templateSettings }) => {
  const brandName = (companyName || 'KUMARA HOTSPOT').toUpperCase();
  const today = new Date().toISOString().split('T')[0];

  const _dCfg = {
    font_brand: 6.0,      // logo label (jika logo tidak tersedia — fallback text)
    font_plan: 6.5,       // price banner "Rp. 40.000 / 30 Hari"
    font_label: 5.5,      // "KODE VOUCHER", meta lines
    font_code: 9.0,       // kode voucher besar
    plan_format: '{price} / {duration}',
    label_text: 'KODE VOUCHER',
    prefix_text: 'Distributed by',
  };
  const cfg = { ..._dCfg, ...(templateSettings || {}) };

  return (
    <div className="print-container-qr print-root">
      <style>{`
        @page { size: A4 landscape; margin: 2mm; }
        @media print {
          html, body { width: 100%; height: 100%; margin: 0 !important; padding: 0 !important; background: white !important; }
          html *:not(.print-root):not(.print-root *) { visibility: hidden !important; }
          .print-root, .print-root * { visibility: visible !important; }
          .print-root {
            position: absolute !important;
            left: 0 !important; top: 0 !important;
            transform: none !important;
            width: 100% !important;
          }
          .print-container-qr { background: white; padding: 0; }
        }

        .print-container-qr {
          width: 297mm;
          margin: 0 auto;
          padding: 2mm;
          background: #ffffff;
          display: flex;
          flex-wrap: wrap;
          gap: 0.6mm;
          align-content: flex-start;
          font-family: 'Arial', sans-serif;
        }

        .qr-card {
          width: calc((100% - 6 * 0.6mm) / 7);
          min-height: 40mm;
          border: 1px dashed #333;
          padding: 1mm 1.2mm 0 1.2mm;
          box-sizing: border-box;
          background: white;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          page-break-inside: avoid;
          flex-shrink: 0;
          overflow: hidden;
        }

        .qr-logo {
          width: 100%;
          text-align: center;
          margin-bottom: 0.6mm;
        }
        .qr-logo img {
          max-width: 28mm;
          max-height: 7.5mm;
          object-fit: contain;
          display: block;
          margin: 0 auto;
        }
        .qr-brand-fallback {
          font-size: ${cfg.font_brand}pt;
          font-weight: 800;
          color: #ff6b35;
          text-align: center;
          letter-spacing: 0.1mm;
        }

        .qr-label {
          font-size: ${cfg.font_label}pt;
          font-weight: 700;
          color: #333;
          text-align: center;
          letter-spacing: 0.15mm;
          margin-top: 0.4mm;
        }

        .qr-code-text {
          font-size: ${cfg.font_code}pt;
          font-weight: 900;
          color: #000;
          text-align: center;
          letter-spacing: 0.4mm;
          margin: 0.5mm 0;
          line-height: 1;
          word-break: break-all;
        }

        .qr-code-img {
          width: 100%;
          display: flex;
          justify-content: center;
          padding: 0.4mm 0;
        }
        .qr-code-img svg {
          width: 14mm;
          height: 14mm;
        }

        .qr-divider {
          width: 100%;
          border-top: 0.4px dashed #999;
          margin: 0.6mm 0 0.4mm 0;
        }

        .qr-meta {
          text-align: center;
          color: #444;
          line-height: 1.2;
          width: 100%;
          padding: 0 0.4mm;
        }
        .qr-meta > div:first-child {
          font-size: ${cfg.font_label}pt;
        }
        .qr-meta-bold {
          font-size: ${cfg.font_brand}pt;
          font-weight: 800;
          color: #000;
        }

        .qr-price-banner {
          margin-top: auto;
          margin-left: -1.2mm;
          margin-right: -1.2mm;
          background: #1c1c1c;
          color: #ff6b35;
          font-size: ${cfg.font_plan}pt;
          font-weight: 900;
          text-align: center;
          padding: 0.9mm 1mm;
          letter-spacing: 0.2mm;
          border-top: 1px dashed #333;
          width: calc(100% + 2.4mm);
        }
      `}</style>

      {batchHeader}
      {vouchers.map((voucher, index) => {
        const code = voucher.voucher_code || voucher.username || '';
        const dur = formatVoucherDuration(voucher);
        const priceStr = voucher.price ? `Rp. ${Number(voucher.price).toLocaleString('id-ID')}` : '';
        const priceLine = (cfg.plan_format || '{price} / {duration}')
          .replace('{price}', priceStr || '')
          .replace('{duration}', dur || '')
          .replace(/^\s*\/\s*|\s*\/\s*$/g, '')
          .trim();
        return (
          <div key={voucher.id || index} className="qr-card">
            <div className="qr-logo">
              {companyLogo
                ? <img src={companyLogo} alt="logo" />
                : <div className="qr-brand-fallback">{brandName}</div>}
            </div>

            <div className="qr-label">{cfg.label_text}</div>
            <div className="qr-code-text">{code}</div>

            <div className="qr-code-img">
              <QRCodeSVG value={buildHotspotLoginUrl(voucher.username || code, voucher.password, hotspotGatewayIp)} size={64} bgColor="#ffffff" fgColor="#000000" level="M" />
            </div>

            <div className="qr-divider" />

            <div className="qr-meta">
              <div>Generated on {today}</div>
              <div className="qr-meta-bold">{cfg.prefix_text} {brandName}</div>
            </div>

            {priceLine && <div className="qr-price-banner">{priceLine}</div>}
          </div>
        );
      })}
    </div>
  );
};

export default QRVoucherTemplate;
